import { HsbVisual } from '../../hsb-visual';
import { CameraView } from '../../isharedata';
import { MeasurementData } from '../Tools/MeasureTool';
import { SectionData } from '../Tools/SectionTool';
import { Viewer } from '../Viewer';

/**
 * Data structure for saved visual data
 */
export type EntityKeyData = {
    hidden: string[],
    highlighted: string[]
}

/**
 * Data structure for saved states of the scene.
 */
export type SceneKey = {
    entities?: EntityKeyData,
    camera?: CameraView,
    selection?: string[],
    measurements?: MeasurementData[],
    section?: SectionData
}

/**
 * Collection of scene keys.
 */
export type SceneReel = {
    name: string
    projectID: string
    keys: SceneKey[];
}

/**
 * Manages anything to do with saved scene keys and scene reels.
 */
export class SceneKeyManager {

    private _loadedReels: SceneReel[] = [];
    public get LoadedReels() {
        return this._loadedReels;
    }
    private _activeReel: SceneReel | null = null;
    public get ActiveReel() { return this._activeReel; }
    private _currentFrame: number = 0;
    private _interval: number = 1;
    public get Interval() { return this._interval; }
    public set Interval(interval: number) {
        this._interval = interval;
    }

    private _rest: number = 1;
    public get Rest() { return this._rest; }
    public set Rest(rest: number) {
        this._rest = rest;
    }
    currentReelOperation: NodeJS.Timeout | null = null;
    currentKeyOperation: Promise<any> | null = null;

    constructor(private _viewer: Viewer) {

    }

    /**
     * Create a new @type {SceneReel}
     * @param activate If true will set the new reel as the active reel.
     * @returns the enwly created reel.
     */
    public createReel(activate?: boolean) {
        if (!this._viewer.project)
            return null;
        var reel: SceneReel = {
            projectID: this._viewer.project.projectID,
            name: "Reel " + this.LoadedReels.length,
            keys: []
        };
        this._loadedReels.push(reel);
        if (activate)
            this._activeReel = reel;
        return reel;
    }
    /**
     * Actives the reel for the given index.
     * @param index 
     * @returns The newly activated reel if found, else undefined.
     */
    public activateReelByIndex(index: number) {
        if (index < 0 || index > this._loadedReels.length - 1) {
            return console.warn("Can't activate reel for index " + index + "!");
        }
        this._activeReel = this._loadedReels[index];
    }
    /**
     * Activate the given reel
     * @param reel 
     * @returns The newly activated reel if found, else undefined.
     */
    public activateReel(reel: SceneReel) {
        var reelIndex = this._loadedReels.findIndex((r) => r === reel);
        if (reelIndex < 0) {
            return console.error("Can't activate reel: " + reel.name + "!");
        }
        this.activateReelByIndex(reelIndex);
    }
    /**
     * Clears all loaded reels.
     */
    public clearReels() {
        if (this._activeReel)
            this._activeReel = null;
        this._loadedReels = [];
    }
    /**
     * Deletes the reel for the given index from teh loaded reels.
     * @param index 
     * @returns the deleted reel if found, undefined otherwise.
     */
    public deleteReelByIndex(index: number) {
        if (index < 0 || index > this._loadedReels.length - 1) {
            return console.warn("Can't delete reel for index " + index + "!");
        }
        if (this._activeReel === this._loadedReels[index])
            this._activeReel = null;
        return this._loadedReels.splice(index, 1);
    }
    /**
     * Deletes the given reel from the loaded reels
     * @param reel 
     * @returns the deleted reel if found, undefined otherwise.
     */
    public deleteReel(reel: SceneReel) {
        var reelIndex = this._loadedReels.findIndex((r) => r === reel);
        if (reelIndex < 0) {
            return console.warn("Can't delete reel: " + reel.name + "!");
        }
        return this.deleteReelByIndex(reelIndex);
    }
    /**
     * Deletes the current active reel if present.
     */
    public deleteActiveReel() {
        if (this._activeReel)
            return this.deleteReel(this._activeReel);
        else
            return console.warn("Can't delete active reel since none was active!");
    }
    /**
     * Adds a scene key to the current active reel for the current state
     */
    public addSceneKey() {
        if (!this._viewer.project)
            return;
        if (!this._activeReel) {
            this._activeReel = {
                projectID: this._viewer.project.projectID,
                name: "Reel " + this.LoadedReels.length,
                keys: []
            };
            this._loadedReels.push(this._activeReel);
        }
        let sk = this.createSceneKey();
        if (sk)
            this._activeReel.keys.push(sk);
    }
    /**
     * Removes the last scene key from the current active reel.
     * @returns The removed key if found, undefined otherwise
     */
    public removeLastSceneKey() {
        if (!this._activeReel)
            return console.warn("Cannot remove scene key without active reel!");
        if (this._activeReel.keys.length === 0)
            return console.warn("There are no keys to remove from " + this._activeReel.name + "!");
        return this._activeReel.keys.splice(this._activeReel.keys.length - 1, 1);
    }

    /**
     * Creates a scene key for the current state
     * @returns The create scenekey if possible, undefined otherwise.
     */
    private createSceneKey(): SceneKey | null {
        if (!this._viewer.currentScene || !this._viewer.project)
            return null;
        // Camera
        var data: any = {}
        // Project
        data.projectID = this._viewer.project.projectID;
        // Camera
        data.camera = this._viewer.getCameraView();
        // Visual
        var entities = this._viewer.project.allHsbEntities();
        data.visuals = { hidden: entities.filter((e) => !e.visible).map((e) => `${e.hsbmodel.modelId}:${e.handle}`), highlighted: entities.filter((e) => e.highlighted).map((e) => e.handle) };


        // Selection
        if (this._viewer._selectionTool?.currentSelection && this._viewer._selectionTool.currentSelection.length > 0)
            data.selection = this._viewer._selectionTool.currentSelection.map((entity) => entity.handle);
        // Measurements
        if (this._viewer._measureTool?.hasMeasurements)
            data.measurements = this._viewer._measureTool.Measurements.map((m) => m.getData());
        // Section
        if (this._viewer._sectionTool?.visible)
            data.section = this._viewer._sectionTool.getData();

        return data as SceneKey;
    }
    /**
     * Creates a prompt to download the current active reel.
     * @returns 
     */
    public downloadActiveReel() {
        if (this._activeReel?.keys.length === 0 || !this._viewer.currentScene)
            return;
        var dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(this._activeReel, null, 1));
        var downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", this._viewer.currentScene.name + ".json");
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    }
    /**
     * Creates a prompt to download the reel for the given index.
     * @param index 
     * @returns 
     */
    public downloadReel(index: number) {
        if (index < 0 || index > this._loadedReels.length - 1) {
            return console.warn("Can't download reel for index " + index + "!");
        }
        if (!this._viewer.currentScene)
            return;
        var reel = this._loadedReels[index];

        var dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(reel));
        var downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", this._viewer.currentScene.name + ".json");
        document.body.appendChild(downloadAnchorNode); // required for firefox
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
    }
    /**
     * Loads the given data in the loaded reels.
     * @param data Data to parse as SceneReel
     * @param autoPlay If true will activate the loaded reel and play it.
     * @returns The newly created reel if properly parsed, undefined otherwise.
     */
    public loadReel(data, autoPlay?: boolean) {
        var reel = JSON.parse(data) as SceneReel;
        if (!reel) {
            console.error("Could not parse scenekey!");
            return;
        }
        this._loadedReels.push(reel);

        if (autoPlay) {
            this._activeReel = reel;
            this.startReel();
        }
        return reel;
    }
    /**
     * Parses data into a new scene key
     * @param data Data to parse as SceneKey
     * @returns Newly created SceneKey if properly parsed, undefined otherwise.
     */
    public loadKey(data: string) {
        var sceneData = JSON.parse(atob(data)) as SceneKey;
        if (!sceneData) {
            return console.error("Could not parse scenekey!");
        }
        return sceneData;
    }

    /**
     * Starts playback of current active reel.
     * @returns True if playback was started, void otherwise.
     */
    public startReel() {
        if (!this._activeReel)
            return console.warn("No reel was active");

        if (this.currentReelOperation) {
            clearTimeout(this.currentReelOperation);
        }

        if (this._activeReel.projectID !== this._viewer.project?.projectID) {
            if (this.currentReelOperation) {
                clearTimeout(this.currentReelOperation);
            }
            return console.warn("Scene data was not intended for this project!");
        }

        this.playReel();
        return true;
    }

    /**
     * Playback method for the current active reel.
     * @returns 
     */
    private playReel() {

        let self = this;
        if (!this._activeReel)
            return;
        var promise = this.applySceneKey(this._activeReel.keys[this._currentFrame]);
        if (promise)
            promise.then((completed: boolean) => {
                // If aborted
                if (!completed) {
                    self._currentFrame = 0
                    return;
                }
                // Else go into rest period 
                self._currentFrame++;
                if (self._currentFrame > self._activeReel!.keys.length - 1) {
                    if (this.currentReelOperation !== null)
                        clearTimeout(self.currentReelOperation!);
                    self._currentFrame = 0;
                    return;
                } else {
                    self.currentReelOperation = setTimeout(self.playReel.bind(self), self.Rest * 1000);
                }
            });
        else {
            self._currentFrame++;
            if (self._currentFrame > self._activeReel!.keys.length - 1) {
                if (this.currentReelOperation !== null)
                    clearTimeout(self.currentReelOperation!);
                self._currentFrame = 0;
                return;
            } else {
                self.currentReelOperation = setTimeout(self.playReel.bind(self), self.Rest * 1000);
            }
        }
    }

    /**
     * Method to apply a scene key to the current scene
     * @param data 
     * @returns 
     */
    private applySceneKey(data: SceneKey) {
        if (!this._viewer.project) {
            if (this.currentReelOperation) {
                clearTimeout(this.currentReelOperation);
            }
            return console.warn("Current scene has no visuals!");
        }

        
        var promise;
        // Camera
        if (data.camera) {
            let cv = this._viewer.cameraUtils.setCameraView(data.camera, true, this.Interval);
            if (cv)
                promise = this.currentKeyOperation = cv;
        }

        // Visuals
        if (data.entities) {
            this._viewer.ShowEverything()

            // Hidden
            let entities = data.entities.hidden.map((handle) => {
                let [modelId, entityHandle] = handle.split(':');
                return this._viewer.project!.EntityTable[modelId][entityHandle]
            });
            this._viewer.HideEntity(entities)

        }
        // Selection
        if (this._viewer._selectionTool) {
            this._viewer._selectionTool.CancelSelection();
            if (data.selection) {
                // var selection = data.selection.map((handle) => visuals.find((v) => v.entity.handle === handle));
                // this._viewer._selectionTool.Select(selection);
            }
        }

        // Measurement
        if (this._viewer._measureTool) {
            this._viewer._measureTool.ClearMeasurements();
            if (data.measurements) {
                this._viewer._measureTool.createFromData(data.measurements);
            }
        }
        //Section
        if (this._viewer._sectionTool) {
            this._viewer._sectionTool.Deactivate();
            if (data.section) {
                this._viewer._sectionTool.Activate();
                this._viewer._sectionTool.placeFromData(data.section);
            }
        }
        return promise ? promise : null;
    }
}